import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Field, SelectField, TextArea } from "@/components/site/FormControls";
import { createServiceRequest, apiError } from "@/lib/api";
import { HOME_INSPECTIONS } from "@/data/homeSite";
import { CtaButton } from "@/components/site/CtaButton";
import { CallButton } from "./HomeSections";

const PTYPES = ["Single-family", "Condo / Townhome", "Multi-family", "Commercial"];
const EMPTY = { full_name: "", phone: "", email: "", address: "", city: "", zip: "", service: "", property_type: "", sqft: "", preferred_date: "", notes: "", preferred_language: "English", website: "" };

export const FinalCta = () => {
  const [f, setF] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const set = (e) => setF({ ...f, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{5}$/.test(f.zip)) return toast.error("Please enter a 5-digit ZIP code.");
    const svc = HOME_INSPECTIONS.find((s) => s.name === f.service);
    setSending(true);
    try {
      setDone(await createServiceRequest({
        full_name: f.full_name, phone: f.phone, email: f.email, address: f.address, city: f.city, state: "PA", zip: f.zip,
        property_type: f.property_type, occupancy: "Unknown", service_id: svc?.id || "", preferred_date: f.preferred_date,
        project_size: f.sqft ? `${f.sqft} sq ft` : "", instructions: f.notes, preferred_language: f.preferred_language, website: f.website,
      }));
    } catch (ex) {
      toast.error(apiError(ex));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="book" className="bg-[#0B132B] py-20 text-white lg:py-24" data-testid="home-final-cta">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 lg:grid-cols-[1fr_1.3fr] lg:px-8">
        <div>
          <h2 className="font-display text-3xl font-bold sm:text-4xl">Ready to book your inspection?</h2>
          <p className="mt-4 text-slate-300">Send the details and we'll confirm price and time before anything is booked.</p>
          <div className="mt-8 flex flex-wrap gap-3"><CtaButton to="/property-inspections/book" size="lg" data-testid="final-book-btn">Book an Inspection</CtaButton><CallButton /></div>
        </div>
        {done ? (
          <div data-testid="home-booking-confirmation" className="rounded-3xl bg-white p-8 text-center text-[#0B132B]">
            <CheckCircle2 className="mx-auto h-12 w-12 text-gold" />
            <p className="mt-4 font-display text-2xl font-bold">Request received</p>
            <p className="mt-2 font-mono-x text-lg tracking-widest" data-testid="home-booking-number">{done.request_number}</p>
            <p className="mt-3 text-sm text-slate-500">Not a confirmed appointment yet. We'll call or email you to confirm.</p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="home-booking-form" className="grid gap-4 rounded-3xl bg-white p-6 text-[#0B132B] sm:grid-cols-2 sm:p-8">
            <Field label="Name" name="full_name" value={f.full_name} onChange={set} required half testid="hb-name" />
            <Field label="Phone" name="phone" type="tel" value={f.phone} onChange={set} required half testid="hb-phone" />
            <Field label="Email" name="email" type="email" value={f.email} onChange={set} required testid="hb-email" />
            <Field label="Property address" name="address" value={f.address} onChange={set} required testid="hb-address" />
            <Field label="City" name="city" value={f.city} onChange={set} required half testid="hb-city" />
            <Field label="ZIP" name="zip" value={f.zip} onChange={set} required half testid="hb-zip" />
            <SelectField label="Inspection type" name="service" value={f.service} onChange={set} options={HOME_INSPECTIONS.map((s) => s.name)} required half testid="hb-service" />
            <SelectField label="Property type" name="property_type" value={f.property_type} onChange={set} options={PTYPES} required half testid="hb-property-type" />
            <Field label="Approx. square feet" name="sqft" type="number" value={f.sqft} onChange={set} half testid="hb-sqft" />
            <Field label="Preferred date" name="preferred_date" type="date" value={f.preferred_date} onChange={set} half testid="hb-date" />
            <SelectField label="Preferred language" name="preferred_language" value={f.preferred_language} onChange={set} options={["English", "Português"]} half testid="hb-language" />
            <TextArea label="Notes" name="notes" value={f.notes} onChange={set} testid="hb-notes" />
            <input type="text" name="website" value={f.website} onChange={set} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
            <button type="submit" disabled={sending} data-testid="hb-submit" className="rounded-full bg-gold py-3.5 font-bold text-[#0B132B] hover:bg-[#E5C158] disabled:opacity-60 sm:col-span-2">{sending ? "Sending…" : "Request My Inspection"}</button>
          </form>
        )}
      </div>
    </section>
  );
};
